export interface FlowNode {
  id: string
  title: string
  subtitle?: string
  owner: 'sales' | 'presales' | 'implementations' | 'customer' | 'gate'
  /** What presales / the owner does for the customer at this stage */
  whatWeDo: string[]
  /** How / process notes */
  howItWorks?: string[]
  /** Why we have this process */
  whyItMatters?: string[]
  /** Key metric / KPI associated with this stage */
  metric?: string
  /** Path to a screenshot/image in public/ */
  image?: string
  /** Additional images (shown in a gallery) */
  images?: string[]
  /** Sub-processes that happen during this stage (e.g., Custom Work, Pipelines & Products) */
  subProcesses?: SubProcess[]
  branch?: { title: string; details: string[] }
}

export interface SubProcess {
  title: string
  details: string[]
  whyItMatters?: string[]
  /** Key metric / KPI associated with this sub-process */
  metric?: string
  /** Path to a screenshot/image in public/ (placeholder until real images are added) */
  image?: string
  /** Additional images (shown in a gallery) */
  images?: string[]
}

export interface ScopeStatus {
  id: string
  label: string
  description: string
  color: string
  bg: string
}

export interface KPI {
  id: string
  label: string
  metric: string
  description: string
  target: string
  how: string[]
}

export const ownerStyles: Record<string, { bg: string; border: string; text: string; dot: string; label: string }> = {
  sales: { bg: 'bg-blue-500/10', border: 'border-blue-500/40', text: 'text-blue-400', dot: 'bg-blue-400', label: 'Sales' },
  presales: { bg: 'bg-purple-500/10', border: 'border-purple-500/40', text: 'text-purple-400', dot: 'bg-purple-400', label: 'Presales' },
  implementations: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', text: 'text-emerald-400', dot: 'bg-emerald-400', label: 'Implementations' },
  customer: { bg: 'bg-amber-500/10', border: 'border-amber-500/40', text: 'text-amber-400', dot: 'bg-amber-400', label: 'Customer' },
  gate: { bg: 'bg-red-500/10', border: 'border-red-500/40', text: 'text-red-400', dot: 'bg-red-400', label: 'Gate' },
}

// ── Main process flow ───────────────────────────────────────────────────────

export const flowNodes: FlowNode[] = [
  {
    id: 'deal-trigger',
    title: 'New Deal / Upsell / Reseller',
    subtitle: 'Sales, Account Management, or Customer Success',
    owner: 'sales',
    whatWeDo: [
      'Sales, Account Management, or Customer Success submits a deal — all go through this flow',
      'Covers new sales, upsells, and reseller deals of any size',
      'Presales is engaged for any deal size',
      'Deal exists in HubSpot with deal stage and probability tracked',
    ],
  },
  {
    id: 'introduce-presales',
    title: 'Introduce Presales',
    subtitle: 'Alitia > $100k / Kumar $0-$100k',
    owner: 'presales',
    whatWeDo: [
      'Help answer questions from the prospect',
      'Advise on high-level solution plan',
      'Provide resources to get started with FrankieOne',
      'Provide access to a Sandbox',
    ],
    howItWorks: [
      'Currently introduced via Slack message or call (depending on customer importance) to prep for demonstration',
      'Advance notice on the problem the customer is trying to solve helps Presales do their best work',
      'Salesperson sets the Solutions Consultant Owner field in HubSpot',
      'Presales acknowledges by filling out the Presales Engineer field',
      'Kumar handles deals from $0 to $100k, Alitia handles deals over $100k',
    ],
    branch: {
      title: 'Introduce Implementations (Jashinta, Andrew, Aju, or Priya)',
      details: [
        'Implementations team is introduced early alongside presales',
        'Not a formal handover yet — awareness and early engagement',
        'Allows implementations to flag concerns early in the process',
      ],
    },
  },
  {
    id: 'presales-activities',
    title: 'Presales Activities',
    subtitle: 'Core presales work',
    owner: 'presales',
    metric: 'KR1: Deals presales were involved in that close (win ratio)',
    whatWeDo: [
      'Answer questions from prospects and sales team',
      'Demonstrations of the FrankieOne platform',
      'High-level solution design',
      'Establish Order Form (Scope in DealHub)',
      'Provide Sandbox access',
      'Define custom development and get items on the roadmap',
      'RFPs/RFIs responses',
      'Establish deadline plan',
    ],
    subProcesses: [
      {
        title: 'Gather the Blueprint',
        metric: '100% of deals over $50k must have a blueprint filled to 100%',
        image: '/process-flow/gather-blueprint.png',
        images: ['/process-flow/gather-blueprint.png', '/process-flow/gather-blueprint-2.png'],
        details: [
          'Sales Context (MEDDPICC): read-only fields pulled from HubSpot — Metrics, Economic Buyer, Decision Criteria, Decision Process, Paper Process, Identify Pain, Champion, Competition',
          'Customer Problem Hypothesis: Presales-owned free-text field to articulate what problem the customer is trying to solve and how FrankieOne helps',
          'As-Is Architecture: documents the customer\'s current integration method (API, iFrame, Portal, SmartUI, other) and links to a Miro board for visual mapping',
          'Discovery Questions: structured prompts covering country coverage, onboarding flow, verification checks, risk differentiation, due diligence requirements, AML screening, ongoing monitoring, and friction points',
          'Current Vendors: captures the customer\'s existing vendor stack to understand what FrankieOne is replacing or complementing',
          'Cascade Builder: visual KYC cascade designer linked from the blueprint — map out capture methods, validation vendors, rulesets, and decision outcomes per country',
          'This information feeds directly into the Order Form (Scope) in DealHub',
        ],
        whyItMatters: [
          'Ensures nothing is missed before scoping begins',
          'Sets the foundation for an accurate order form',
          'Reduces back-and-forth during scope review',
          'Helps presales give better solution advice',
        ],
      },
      {
        title: 'Order Form (Scope)',
        metric: '100% of deals (any value) must have a scope',
        image: '/process-flow/order-form.png',
        details: [
          'Scope is linked to a HubSpot deal and tracks the customer name, ABN, and deal reference',
          'Scope status lifecycle: Draft → Sent → Adjust → Confirm → Handover — with dates tracked at each stage',
          'Overall progress percentage tracks how complete the scope is across all configuration sections',
          'Blueprint progress tracked separately — captures discovery and as-is architecture completion',
          'Configuration sections with individual completion tracking: General, KYC, AML, ID Verification, KYB, Recipes/Workflows, Risk & Fraud, and more',
          'Each section contains detailed fields for the customer\'s chosen configuration (e.g., AML Check Level, screening vendors, monitoring settings)',
          'DealHub enforces 100% completion before a scope can move to Confirmed status',
          'Make the customer aware of the options they have with FrankieOne',
          'Help the customer make decisions on their solution in their best interests',
          'Highlight areas where the customer still needs to make a decision',
        ],
        whyItMatters: [
          'Set better expectations with the customer',
          'Reduce strain on Implementations team',
          'Reduce implementations scope creep',
          'Improve handover to internal teams',
        ],
      },
      {
        title: 'Sandbox',
        image: '/process-flow/sandbox.png',
        images: ['/process-flow/sandbox.png', '/process-flow/sandbox-2.png'],
        details: [
          'Presales distributes a sandbox account from a provisioning list (Google Sheet) — accounts are pre-configured and ready to use',
          'An email is sent to the customer with portal credentials (username/password), API credentials via a 1Password link, and a V2 API Integration Guide (PDF) to get started',
          'Email includes housekeeping notes: sandbox is shared tenancy, no real IDs, data wiped every Friday at 10 PM Melbourne time, API headers change when transitioning to UAT/Production',
          'Also includes a link to the FrankieOne MCP server for customers using AI/Vibe Coders — connects their AI to FrankieOne API docs',
          'Sandbox portal shows entities, needs review queue, analytics, blocklist, workflows, risk and rules — the full FrankieOne experience',
          'No more waiting for UAT accounts to be created, or bug fixes',
          'Protects privacy by wiping data regularly, distributing temporary email access that can be rescinded',
          'No more bespoke accounts — implementations will create the custom UAT once the customer is handed over',
        ],
        whyItMatters: [
          'Improve speed of access, reduce bugs',
          'Smoother implementations handover',
          'Help presales focus on selling',
          'Better customer experience',
        ],
      },
      {
        title: 'Deadlines',
        image: '/process-flow/deadlines-3.png',
        images: ['/process-flow/deadlines-3.png', '/process-flow/deadlines.png', '/process-flow/deadlines-2.png'],
        details: [
          'Deadlines are captured by the presales team in the scope — Production Access Date and Expected Go-Live Date (with a "Hard date" checkbox for firm client commitments)',
          'The customer selects their deadlines, and the entire project plan is built around them',
          'Deadlines are confirmed with the Implementations team to ensure they are achievable',
          'Deadlines are confirmed by the customer when they sign the Statement of Work',
          'Gantt Chart in the SOW breaks the project into tasks with responsible party (FrankieOne or Client), start and end dates — e.g., Project Kick-off, Vendor Forms, UAT Provisioning, API Integration, SIT/UAT Testing, Prod Provisioning, Production Access, Go-Live Deployment, Hypercare Period',
          'Project Phases define what is being delivered per phase (e.g., KYC vendors, AML screening type, monitoring frequency) with start/end dates',
          'Help the customer determine if their deadline is realistic from their side',
          'Set conditions on how FrankieOne can achieve the deadline',
          'Hard deadlines must be signed off by Implementations',
          'Customer should be required to tell us if their dates move, so we can move ours for a build',
          'Being more realistic on when the customer will actually be using the feature',
        ],
        whyItMatters: [
          'Set better expectations with the customer',
          'Reduce strain on Implementations team',
          'Ensure customer is aware that they contribute to deadline success',
        ],
      },
      {
        title: 'Custom Work — Pipelines & Products',
        metric: '100% of deals must go through Pipelines & Products',
        image: '/process-flow/custom-work.png',
        images: ['/process-flow/custom-work.png', '/process-flow/pipelines-products.png'],
        details: [
          'Determine if custom work is a requirement',
          'Determine the scope of the custom work',
          'New ideas are submitted via a Claude AI project ("Submit Product Ideas") for assessment — captures sales-driven requests into FDIS with HubSpot context',
          'Must include a HubSpot link (to attach revenue) and whatever info on the request is necessary',
          'Ideas are tracked in Jira (FrankieOne Discovery board) and visible to anyone',
          'Scope is provided by Presales and Customer in a Jira ticket, including the ideal delivery date',
          'PM confirms scope — needs to know the WHY, urgency, and ROI',
          'PM confirms if it will be placed on the roadmap, and the delivery date',
          'Only when work is on the roadmap can FrankieOne confirm to the client it will be done as part of the sale process',
          'Introduce the Product Manager to discuss the work with the customer',
          'Implementations team will manage the delivery of custom work so must sign off on it',
          'Custom work is discussed at the weekly Pipelines & Products meeting',
          'Discuss any changes in priority or delivery dates with the customer to set expectations',
        ],
        whyItMatters: [
          'Set better expectations with the customer',
          'Reduce strain on implementations team chasing the tech team',
          'Hold product accountable for prioritisation changes that affect contract work',
          'Raise quality of custom work with better scoping',
        ],
      },
      {
        title: 'Checking Scope — Pipelines & Products',
        metric: '100% of deals must go through Pipelines & Products',
        image: '/process-flow/pipelines-products.png',
        details: [
          'Weekly cross-business meeting at the start of each week',
          'Presales assigns deals to be discussed via the Pipelines & Products tool in DealHub',
          'Go through the DealHub tool to review assigned deals as a group',
          'Go through scopes of deals about to be handed over to ensure all features are out of the box (standard / OOB)',
          'Each scope card shows: integration type, platform version, KYC/IDV/AML/Fraud/Risk/KYB configuration, key dates, and notes',
          'Checkboxes to mark deals as "Discussed" and "Standard / OOB" during the meeting',
          'Sense-check deals and confirm whether they are standard/out-of-the-box or require custom work',
          'Discuss upcoming RFIs that require PM assistance',
          'Discuss notable deadlines for go-lives and whether resource requirements from teams are needed',
        ],
      },
      {
        title: 'Ensuring Quality & Scalability',
        image: '/process-flow/quality-scalability.png',
        details: [
          'Presales Dashboard (Operations tab) provides a real-time view of all deals in pre-handover and post-handover stages',
          'Each deal shows: Deal Actions progress (scored out of 10), hours tracked vs estimated, scope completion %, last activity date, and deal stage',
          'Filters for deal status (Open, Won), owner, assigned-only, and partner deals to focus on what matters',
          'Visual donut charts per deal show which actions are complete (scope, blueprint, sandbox, custom work, etc.)',
          'Used in a weekly presales and implementations meeting to visualise what is likely to hit the implementations team and when',
          'Ensures presales is always producing quality scopes that are smooth for the implementations team to pick up',
          'Identifies deals that are falling behind on scope completion or missing key actions before they become a problem',
          'Enables the team to proactively manage workload and resource allocation across upcoming handovers',
        ],
        whyItMatters: [
          'Smooth handovers to implementations',
          'Early visibility into upcoming workload',
          'Quality control on presales deliverables',
          'Data-driven presales operations',
        ],
      },
    ],
  },
  {
    id: 'stage-gate',
    title: 'Stage 4 AND Probability > 80%',
    owner: 'gate',
    whatWeDo: [
      'HubSpot deal must be at Stage 4 (Contract Sent) or later',
      'Probability of closure must exceed 80%',
      'Both conditions must be met before implementations can formally engage',
      'Sales team maintains both the deal stage and probability fields',
    ],
  },
  {
    id: 'intro-implementations',
    title: 'Introduce Implementations',
    subtitle: 'Not necessarily 100% defined',
    owner: 'implementations',
    whatWeDo: [
      'Implementations team formally brought into the deal',
      'Scope does not need to be 100% complete at this point',
      'Allows implementations to begin planning and resource allocation',
      'Customer is introduced to their implementation team',
    ],
  },
  {
    id: 'sow-created',
    title: 'SOW is Created',
    subtitle: 'Implementations to create',
    owner: 'implementations',
    image: '/process-flow/sow-builder.png',
    images: [
      '/process-flow/sow-builder.png',
      '/process-flow/sow-legal-text.png',
      '/process-flow/sow-phases-sequence.png',
      '/process-flow/sow-scope-summary.png',
    ],
    whatWeDo: [
      'Statement of Work is created by the Implementations team in the SOW tab of DealHub',
      'Based on the order form/scope established during presales activities',
      'Order form and SOW will be used to generate more accurate implementation fee projections',
      'Adonis has made adjustments to contracts to account for contingency — if the scope varies greatly from the original contract, there is a vehicle to request additional funds',
    ],
    howItWorks: [
      'SOW is built in DealHub with collapsible sections: Legal Text, Delivery Notes, Phases & Variation, Integration Sequence Diagram, RACI Chart, Gantt Chart, Key Assumptions, and Additional Notes',
      'Legal Text: standard preamble covering Purpose and Scope, Scope of Services (project planning, environment configuration, technical integration support, compliance alignment, user enablement, post-go-live support), and out-of-scope clauses',
      'Scope of Services includes: IDV, branding configuration, KYC data sources, AML screening rules, transaction monitoring, Customer Risk Rating, and orchestration workflows',
      'Project Phases: defines scope per phase (e.g., KYC vendors, AML screening type, monitoring frequency) with start/end dates',
      'Integration Sequence Diagram: visual diagram showing the end-to-end API flow between the customer\'s frontend, backend, FrankieOne, and data sources',
      'Scope Configuration Summary: the full technical scope (order form) is embedded as a PDF-ready document — every field, selected value, and pending decision is included',
      'Sections can inherit from a global SOW template or be customised per deal',
      'SOW is sent to the customer via Annature (electronic signature platform) for review and signing',
      'Customer signs the SOW electronically via Annature — this constitutes the formal handover point',
      'Once the SOW is signed, the deal is considered formally handed over to implementations',
    ],
  },
  {
    id: 'handover-gate',
    title: 'Deal Value > $5,000?',
    owner: 'gate',
    metric: 'KR2: Presales Cycle Speed — days from presales engagement to formal handover (SOW signed)',
    whatWeDo: [
      'Once the SOW is signed by the customer via Annature, the deal is formally handed over',
      'Handover date is tracked as a key metric (KR2: Presales Cycle Speed)',
      'The handover path depends on the deal value',
    ],
    howItWorks: [
      'Deals over $5,000: formally handed over to the Implementations team',
      'Deals $5,000 or under: SOW is handed back to the Customer Success team, who work with Tech Ops to implement',
    ],
    branch: {
      title: 'CS + Tech Ops (deals ≤ $5k)',
      details: [
        'SOW is handed back to the Customer Success team',
        'CS team works with Tech Ops to do the implementation',
        'Lighter-touch implementation path for smaller deals',
      ],
    },
  },
  {
    id: 'formal-handover',
    title: 'Formal Handover',
    subtitle: 'Deals > $5k → Implementations',
    owner: 'presales',
    whatWeDo: [
      'Once order form is 100% known, Presales hands the customer to Implementations',
      'The SOW is signed by the customer via Annature — this is the formal handover moment',
      'Introduce the customer to the implementation team (Jashinta, Andrew, Aju, or Priya)',
      'Discuss their goals and success criteria',
      'Go over the order form (should be at 100%)',
      'Review custom work requirements and status',
      'Agree on deadlines and milestones',
    ],
  },
  {
    id: 'implementations',
    title: 'Implementations',
    owner: 'implementations',
    whatWeDo: [
      'Creates UAT environment for customer testing',
      'Creates Production environment',
      'Builds configuration per the signed scope',
      'Delivers custom development work',
      'Manages customer through to go-live',
    ],
    howItWorks: [
      'Actual go-live dates tracked with customers',
      'UAT and production environment dates managed',
      'SOW may be updated as details become clearer during implementation',
    ],
  },
]

// ── Scope status lifecycle ──────────────────────────────────────────────────

export const scopeStatuses: ScopeStatus[] = [
  {
    id: 'draft',
    label: 'Draft',
    description: 'Scope is being built — fields are being filled out through discovery and customer conversations.',
    color: 'text-slate-400',
    bg: 'bg-slate-500/10',
  },
  {
    id: 'sent',
    label: 'Sent',
    description: 'Scope has been sent to the customer for review. Awaiting feedback.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
  {
    id: 'adjust',
    label: 'Adjust',
    description: 'Customer has requested changes — scope is being revised based on feedback.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
  },
  {
    id: 'confirm',
    label: 'Confirmed',
    description: 'Customer has agreed to the scope. All required fields are 100% complete (DealHub enforced).',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  {
    id: 'handover',
    label: 'Handover',
    description: 'Scope has been handed over to the Implementations team. Presales involvement winds down.',
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
  },
  {
    id: 'implementations',
    label: 'Impl',
    description: 'An implementations manager is assigned and the SOW is being prepared for the customer.',
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
  },
]

// ── Presales KPIs (Key Results) ─────────────────────────────────────────────

export const kpis: KPI[] = [
  {
    id: 'kr1',
    label: 'KR1: Win Ratio',
    metric: '% of deals closed-won vs closed-lost',
    description: 'Tracks the percentage of deals where presales was involved that ended up being won. Captured during Presales Activities — any deal presales is involved in that closes contributes to this metric.',
    target: 'Continuously tracked and reported',
    how: [
      'Calculated as won_count / (won_count + lost_count)',
      'Only includes deals where a Presales Engineer is assigned',
      'Captured during Presales Activities stage — presales involvement is the trigger',
      'Deals attributed to the quarter their presales engagement started',
      'Broken down by individual presales engineer (Alitia, Kumar)',
    ],
  },
  {
    id: 'kr2',
    label: 'KR2: Presales Cycle Speed',
    metric: 'Average days from presales engagement to formal handover (SOW signed)',
    description: 'Measures how quickly presales moves deals through to implementations. Handover is tracked when the SOW is signed by the customer via Annature — this is the formal handover point.',
    target: '15% reduction quarter-over-quarter (baseline ~54 days)',
    how: [
      'Calculated as: Handover Date (SOW signed) minus Presales Start Date',
      'Start date = earliest of: SC Owner Assigned, Presales Engagement Date, or First Demo Date',
      'Handover date = when the customer signs the SOW via Annature',
      'Reports percentiles (p50, p75, p90) for distribution insight',
      'Broken down by presales engineer',
    ],
  },
  {
    id: 'kr3',
    label: 'KR3: Demo Capacity & Utilisation',
    metric: 'Demo count and team utilisation ratio',
    description: 'Tracks how many demos each presales engineer conducts and whether the team is operating at capacity.',
    target: 'Baseline: 12 demos per person per month',
    how: [
      'Sourced from calendar events linked to deals via the Calendar Tracker extension',
      'Tracks capacity vs actuals and utilisation percentage',
      'Monthly breakdown per engineer',
      'Includes manual pre-calendar-integration data (before Feb 16, 2026)',
    ],
  },
  {
    id: 'kr4',
    label: 'KR4: Presales Deliverables Completion',
    metric: '% completion of presales deliverables',
    description: 'Tracks whether presales deliverables (config docs, test cases, training materials) are being completed for each deal.',
    target: 'High completion rate per quarter',
    how: [
      'Calculated as completed_count / total_count',
      'Deliverables include: configuration documents, test cases, training materials',
      'Each deliverable tracked as: pending, in_progress, completed, or skipped',
      'Reported by quarter',
    ],
  },
]

// ── Swimlane data ───────────────────────────────────────────────────────────

export interface SwimLaneRow {
  phase: string
  sales?: string
  presales?: string
  customer?: string
  implementations?: string
}

export const swimLaneData: SwimLaneRow[] = [
  {
    phase: 'Deal Trigger',
    sales: 'Sales, Account Management, or Customer Success submits a deal (new sale, upsell, or reseller)',
  },
  {
    phase: 'Engagement',
    sales: 'Assigns presales (Kumar $0-$100k / Alitia >$100k), introduces implementations early',
    presales: 'Acknowledges assignment, reviews deal context, preps for demo',
    implementations: 'Early introduction (Jashinta, Andrew, Aju, or Priya) for awareness',
  },
  {
    phase: 'Presales Activities',
    sales: 'Joins calls, manages relationship',
    presales: 'Demos, solution design, order form/scope, sandbox, custom work scoping, RFPs, deadline planning',
    customer: 'Shares requirements, attends demos, tests sandbox, makes decisions on configuration',
  },
  {
    phase: 'Pipelines & Products',
    sales: 'Joins weekly meeting, provides deal context',
    presales: 'Assigns deals for review, presents scope status, raises custom work needs',
    implementations: 'Confirms out-of-the-box vs custom, signs off on custom work delivery',
  },
  {
    phase: 'Stage Gate',
    sales: 'Maintains deal stage (Stage 4+) and probability (>80%) in HubSpot',
  },
  {
    phase: 'Introduce Impl.',
    presales: 'Facilitates introduction, shares scope progress',
    customer: 'Meets implementation team',
    implementations: 'Formally engaged, begins planning (scope may not be 100%)',
  },
  {
    phase: 'SOW Creation',
    implementations: 'Creates SOW (legal text, RACI, phases, Gantt, scope, assumptions)',
    customer: 'Reviews and signs SOW via Annature (electronic signature)',
  },
  {
    phase: 'Handover Gate',
    presales: 'SOW signed = formal handover (KR2 tracked). Deal value determines path',
    customer: 'Signs SOW via Annature',
  },
  {
    phase: 'Formal Handover (>$5k)',
    presales: 'Walks through scope (100%), custom work, goals, deadlines with implementations',
    customer: 'Confirms expectations and timelines',
    implementations: 'Receives full context, confirms understanding',
  },
  {
    phase: 'CS + Tech Ops (≤$5k)',
    sales: 'CS team receives SOW back, works with Tech Ops to implement',
    customer: 'Works with CS team through to go-live',
  },
  {
    phase: 'Implementation',
    implementations: 'Creates UAT + production environments, builds and delivers (deals >$5k)',
    customer: 'Tests in UAT, provides sign-off, goes live',
  },
]

export const swimLaneOwners = ['sales', 'presales', 'customer', 'implementations'] as const
